/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import React from 'react';
import { TONES, TONE_DO_STATUS } from '../paleta';
import { ST_META } from '../utils/ordemStatus';
import type { StatusEsteira } from '../api';

/**
 * Lista lateral de grupos/etapas da aba Processos — espelho 1:1 do protótipo
 * (`sistema/index.html` linhas 177-224). No protótipo ela só é RENDERIZADA no nível
 * nano ("Etapas do bot") — `mostrarListaLateral: !!proc.tlNodes`; a versão de
 * macro/board era código morto e não migra (auditoria C2 §1.5).
 */

export interface LinhaLateral {
  key: string;
  name: string;
  sub: string;
  st: StatusEsteira;
  m1: string | number;
  m2: string | number;
  m3: string | number;
  action: string;
  onSelect: () => void;
}

export interface GrupoLateral {
  num: string;
  name: string;
  meta: string;
  right: string;
  headBg: string;
  rows: LinhaLateral[];
}

interface ListaLateralProps {
  title: string;
  sub: string;
  groups: GrupoLateral[];
  m1Label: string;
  m2Label: string;
  m3Label: string;
}

const Metrica: React.FC<{ label: string; valor: string | number; minWidth: string }> = ({
  label,
  valor,
  minWidth,
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth }}>
    <div
      style={{
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: '9.5px',
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: 'oklch(0.66 0.012 165)',
      }}
    >
      {label}
    </div>
    <div style={{ fontSize: '13px', fontWeight: 600 }}>{valor}</div>
  </div>
);

export const ListaLateral: React.FC<ListaLateralProps> = ({
  title,
  sub,
  groups,
  m1Label,
  m2Label,
  m3Label,
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0 }}>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
      <div style={{ fontSize: '16px', fontWeight: 600 }}>{title}</div>
      <div style={{ fontSize: '12.5px', color: 'oklch(0.55 0.015 165)' }}>{sub}</div>
    </div>
    {groups.map(g => (
      <div
        key={`${g.num}-${g.name}`}
        style={{
          background: 'oklch(1 0 0)',
          border: '1px solid oklch(0.92 0.006 160)',
          borderRadius: '12px',
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
        }}
      >
        <div
          style={{
            padding: '13px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px 16px',
            flexWrap: 'wrap',
            background: g.headBg,
            borderRadius: '12px 12px 0 0',
            borderBottom: '1px solid oklch(0.93 0.008 160)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: '10px',
              minWidth: 0,
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: '11px',
                fontWeight: 600,
                color: 'oklch(0.5 0.09 165)',
              }}
            >
              {g.num}
            </span>
            <span style={{ fontSize: '14.5px', fontWeight: 600 }}>{g.name}</span>
            <span style={{ fontSize: '12px', color: 'oklch(0.58 0.015 165)' }}>{g.meta}</span>
          </div>
          <div
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: '11px',
              color: 'oklch(0.58 0.015 165)',
            }}
          >
            {g.right}
          </div>
        </div>
        {g.rows.map(r => {
          const meta = ST_META[r.st] ?? ST_META.wait;
          const t = TONES[TONE_DO_STATUS[r.st] ?? 'idle'];
          return (
            <div
              key={r.key}
              onClick={r.onSelect}
              style={{
                padding: '13px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px 16px',
                flexWrap: 'wrap',
                borderBottom: '1px solid oklch(0.96 0.006 160)',
                boxShadow: `inset 3px 0 0 ${t.dot}`,
                cursor: 'pointer',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px',
                  flex: '1 1 210px',
                  minWidth: '190px',
                }}
              >
                <div style={{ fontSize: '13.5px', fontWeight: 600 }}>{r.name}</div>
                <div
                  style={{
                    fontSize: '11.5px',
                    color: 'oklch(0.58 0.015 165)',
                    textWrap: 'pretty',
                  }}
                >
                  {r.sub}
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px 16px',
                  flexWrap: 'wrap',
                  flex: '0 1 auto',
                }}
              >
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 10px',
                    borderRadius: '20px',
                    background: t.bg,
                    color: t.fg,
                    fontSize: '11.5px',
                    fontWeight: 600,
                    flex: 'none',
                  }}
                >
                  <span
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      background: t.dot,
                    }}
                  />
                  {meta.label}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <Metrica label={m1Label} valor={r.m1} minWidth="62px" />
                  <Metrica label={m2Label} valor={r.m2} minWidth="54px" />
                  <Metrica label={m3Label} valor={r.m3} minWidth="54px" />
                </div>
                <div
                  style={{
                    padding: '7px 13px',
                    borderRadius: '8px',
                    border: '1px solid oklch(0.92 0.006 160)',
                    fontSize: '12.5px',
                    fontWeight: 600,
                    color: 'oklch(0.42 0.05 165)',
                    flex: 'none',
                  }}
                >
                  {r.action}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    ))}
  </div>
);
